import { LoaderCircle } from 'lucide-react';
import React from 'react';
import cn from './primitives/cn';

/**
 * What a list shows while its records are on their way.
 *
 * Tables used to print their empty message ("There are currently no collection items")
 * for as long as the first request took, so a slow list read as an empty one and then
 * filled in seconds later. This is the one loading look for every table family (the
 * Bootstrap `.table`, MUI's `Table` and the data grid): a turning ring and the word, in
 * the ink and size of the empty message it stands in for. It sits in the same cell the
 * empty message does, so the row keeps its height whichever way the request settles.
 *
 * It is a block-level flex row that inherits the cell's type rather than setting its own.
 * As `inline-flex` it sat on the text baseline and grew the line box, so the loading row
 * came out 4px taller than the empty row and the table twitched when a list came back
 * with nothing in it.
 *
 * `role="status"` is a polite live region, so a screen reader hears "Loading…" once
 * rather than nothing. The ring only turns for readers who have not asked for reduced
 * motion; the word carries the state on its own.
 */
const Loading = ({ label = 'Loading…', className }) => (
  <span
    role="status"
    className={cn('tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-muted', className)}
  >
    <LoadingRing size={16} />
    <span>{label}</span>
  </span>
);

/**
 * The ring on its own, for a spinner that sits beside something rather than in place of
 * it: the page title, a list toolbar, the Publish button. It replaced `img/spinner.gif`,
 * the grey spokes, so a screen that is loading shows one spinner rather than two styles
 * of one. Decorative: the words, where there are any, come from `Loading` or the screen.
 */
export const LoadingRing = ({ className, ...rest }) => (
  <LoaderCircle
    aria-hidden="true"
    className={cn('tw-shrink-0 tw-text-muted motion-safe:tw-animate-spin', className)}
    {...rest}
  />
);

export default Loading;
